/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {FieldErrors, UseFormRegister} from 'react-hook-form'

type UiSwitchSize = 'sm' | 'md'

type UiSwitchProps = {
    name: string
    label?: React.ReactNode
    description?: React.ReactNode
    register?: UseFormRegister<any>
    errors?: FieldErrors
    required?: boolean
    checked?: boolean
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    disabled?: boolean
    size?: UiSwitchSize
    className?: string
}

const UiSwitch: React.FC<UiSwitchProps> = ({
    name,
    label,
    description,
    register,
    errors,
    required,
    checked,
    onChange,
    disabled,
    size = 'md',
    className,
}) => {
    const hasError = Boolean(errors && errors[name])
    const message = hasError ? errors[name]?.message : undefined

    const trackSize = size === 'sm' ? 'h-5 w-9' : 'h-6 w-11'
    const knobSize =
        size === 'sm'
            ? 'h-4 w-4 translate-x-0.5 peer-checked:translate-x-4.5'
            : 'h-5 w-5 translate-x-0.5 peer-checked:translate-x-5.5'

    return (
        <div className={clsx('UiSwitch w-full', className)}>
            <label className={clsx('flex items-start gap-3 text-gray-900')}>
                <span className={clsx('relative inline-flex items-center')}>
                    <input
                        {...(register ? register(name, { required }) : {})}
                        name={name}
                        type={'checkbox'}
                        checked={checked}
                        onChange={onChange}
                        disabled={disabled}
                        className={clsx('peer sr-only')}
                    />
                    <span
                        className={clsx(
                            'block rounded-full bg-gray-200 transition-colors peer-focus:outline-none border-2 border-transparent peer-focus:border-arbo-blue peer-checked:bg-arbo-blue',
                            trackSize,
                            hasError && 'border-red-500'
                        )}
                    />
                    <span
                        className={clsx(
                            'pointer-events-none absolute top-1/2 -translate-y-1/2 rounded-full bg-white shadow transition-transform',
                            knobSize
                        )}
                    />
                </span>
                <span className={clsx('flex-1')}>
                    <span className={clsx('block text-sm font-medium')}>
                        {label}
                    </span>
                    {description && (
                        <span className={clsx('block text-xs text-gray-500')}>
                            {description}
                        </span>
                    )}
                </span>
            </label>
            {message && (
                <div className={clsx('mt-1 text-xs text-red-600')}>
                    {message as React.ReactNode}
                </div>
            )}
        </div>
    )
}

export default UiSwitch
